/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-props-destruction */
/* eslint-disable no-prototype-builtins */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import SourceCategorySortContainer from 'SourceComponent/CategorySort/CategorySort.container';

import CategorySortComponent from './CategorySort.component';

/**
 * Category sort container
 * @class CategorySortContainer
 * @namespace Scandipwa/Component/CategorySort/Container */
export class CategorySortContainer extends SourceCategorySortContainer {
    containerFunctions = () => ({
        getSelectedLabel: this.getSelectedLabel.bind(this)
    });

    /**
     * Returns label for each sort key
     *
     * @param option
     * @returns {{asc: *, desc: *}|{asc: *}}
     * @private
     */
    _getLabel(option) {
        const { id, label: pureLabel } = option;

        // eslint-disable-next-line fp/no-let
        let [label] = pureLabel.split(' ');
        label = label.charAt(0).toUpperCase() + label.slice(1);

        switch (id) {
        case 'name':
            return {
                asc: __('Product Name: asc'),
                desc: __('Product Name: desc')
            };
        case 'position':
            return {
                asc: __('Latest Products')
            };
        case 'online_from':
            return {
                desc: __('Latest Products')
            };
        case 'price':
            return {
                asc: __('Price Range: Lowest'),
                desc: __('Price Range: Highest')
            };
        default:
            return {
                asc: __('%s: Ascending', label),
                desc: __('%s: Descending', label)
            };
        }
    }

    /**
     * Returns options for each sort key
     *
     * @param option
     * @returns {{asc: *, desc: *}|{asc: *}}
     * @private
     */
    _prepareOptions() {
        const {
            sortFields,
            default_sort_by
        } = this.props;

        if (!sortFields) {
            return [];
        }

        const optionToRemove = default_sort_by === 'position' ? 'online_from' : 'position';

        const selectOptions = sortFields.reduce((acc, option) => {
            const { id } = option;

            const lowerCaseId = id.toLowerCase();

            if (
                (lowerCaseId === 'online_from' || lowerCaseId === 'position')
                && lowerCaseId === optionToRemove
            ) {
                return acc;
            }

            const label = this._getLabel(option);
            const { asc, desc } = label;

            if (asc) {
                acc.push({
                    id: `ASC ${id}`,
                    name: id,
                    value: `ASC ${id}`,
                    label: asc
                });
            }

            if (desc) {
                acc.push({
                    id: `DESC ${id}`,
                    name: id,
                    value: `DESC ${id}`,
                    label: desc
                });
            }

            return acc;
        }, []);

        const sortingOrder = {
            online_from: 1,
            position: 2,
            price: 3
        };

        selectOptions.sort(this._compareByListOrder('name', sortingOrder));
        return selectOptions;
    }

    /**
     * Returns selected sort label
     *
     * @param selectedSort
     * @returns {string}
     */
    getSelectedLabel(selectedSort) {
        const selectOptions = this._prepareOptions();

        return Object.entries(selectOptions).reduce((acc, [, option]) => {
            const { id, label } = option;

            if (id === selectedSort) {
                return label;
            }

            return acc;
        }, '');
    }

    /**
     * sort function by order of array
     * ex : list = list.sort(_compareByListOrder('keyname',{'items1':1,'items2':2}))
     * @param key
     * @param sortingOrder
     * @returns int
     */
    _compareByListOrder(key, sortingOrder, order = 'asc') {
        return function (a, b) {
            if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
                return 0;
            }

            const first = (a[key].toLowerCase() in sortingOrder) ? sortingOrder[a[key]] : Number.MAX_SAFE_INTEGER;
            const second = (b[key].toLowerCase() in sortingOrder) ? sortingOrder[b[key]] : Number.MAX_SAFE_INTEGER;

            let result = 0;
            if (first < second) {
                result = -1;
            } else if (first > second) {
                result = 1;
            }

            // eslint-disable-next-line no-bitwise
            return (order === 'desc') ? ~result : result;
        };
    }

    render() {
        return (
            <CategorySortComponent
              { ...this.props }
              { ...this.containerProps() }
              { ...this.containerFunctions() }
            />
        );
    }
}

export default CategorySortContainer;
